import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon, IconButton } from "@qota/ui";
import { INSET_TOP } from "../lib/insets";
import { markBack } from "../lib/transitions";

/**
 * La testata dell'app. **Una sola**, per tutte le schermate.
 *
 * Prima ce n'erano due che non andavano d'accordo: `Screen` con indietro da
 * 40px e titolo da 19px ma senza posto per azioni né sottotitolo, e `TopBar`
 * con indietro da 38px, titolo da 17px, sottotitolo e azioni. Non era una
 * differenza estetica: era il motivo per cui la scheda di una persona non
 * poteva avere un pulsante.
 *
 * ── Come si comporta ──────────────────────────────────────────────────────
 *
 * A riposo il titolo è **grande e a tutta larghezza**: un nome di gruppo con
 * le emoji, o «Bolletta Luce LUG 2026», ci sta per intero invece di troncarsi
 * contro le azioni.
 *
 * Scorrendo, il titolo grande esce di scena e **ricompare in piccolo** nella
 * barra, che resta in cima. Dentro un gruppo ci sono centosettantasei voci: con
 * una testata che scorre via, tornare indietro vuol dire risalirle tutte.
 *
 * **Indietro e azioni non si muovono mai** — sono nella barra fissa fin
 * dall'inizio. Un secondo pulsante indietro che compare scorrendo sarebbe due
 * elementi con lo stesso nome per chi naviga da tastiera o con uno screen
 * reader.
 *
 * ── Perché non c'è un `<header>` attorno ──────────────────────────────────
 *
 * `position: sticky` è vincolato al **riquadro del proprio genitore**: un
 * elemento incollato non esce mai dai confini di chi lo contiene. Avvolgendo
 * questi pezzi in un `<header>` alto un centinaio di pixel, la barra restava in
 * cima per quei cento pixel e poi se ne andava insieme a lui — che è
 * esattamente ciò che sembrava un difetto dello scorrimento.
 *
 * I pezzi sono quindi fratelli diretti del contenitore alto quanto la pagina,
 * e la barra si incolla per tutta la sua altezza.
 */
export interface AppHeaderProps {
  title: string;
  subtitle?: ReactNode;
  onBack?: () => void;
  /** Icone a destra, sempre raggiungibili. */
  actions?: ReactNode;
}

export function AppHeader({ title, subtitle, onBack, actions }: AppHeaderProps) {
  const sentinel = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    // Un osservatore invece di un ascoltatore sullo scorrimento: quello
    // scatterebbe a ogni fotogramma per rispondere a una domanda da sì o no.
    const observer = new IntersectionObserver(
      ([entry]) => setCollapsed(entry !== undefined && !entry.isIntersecting),
      // La sentinella «esce» quando passa sotto la barra, non quando esce
      // dallo schermo: altrimenti il titolo piccolo comparirebbe troppo tardi.
      { rootMargin: "-56px 0px 0px 0px", threshold: 0 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          gap: 12,
          // Il guscio dà al contenuto lo spazio della barra di stato. Qui lo si
          // toglie e lo si rimette come proprio riempimento: a riposo non
          // cambia niente, ma da incollata la testata copre anche quella
          // striscia, invece di lasciarci scorrere sotto il contenuto.
          marginTop: `calc(-1 * (${INSET_TOP}))`,
          paddingTop: INSET_TOP,
          paddingBottom: 10,
          background: "var(--surface-canvas)",
        }}
      >
        {onBack && (
          <IconButton
            tone="light"
            size={40}
            label="Indietro"
            // La direzione va dichiarata *prima* della navigazione: è in quel
            // momento che il browser fotografa la pagina da animare.
            onClick={() => {
              markBack();
              onBack();
            }}
          >
            <Icon name="arrow-left" size={19} />
          </IconButton>
        )}

        <span
          className="qota-header-title"
          // Sempre nascosto agli assistenti vocali: è l'eco visiva del titolo
          // qui sotto, e da incollata sarebbero due volte la stessa cosa.
          aria-hidden="true"
          style={{
            flex: 1,
            minWidth: 0,
            fontFamily: "var(--font-display)",
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: "-0.02em",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: collapsed ? 1 : 0,
          }}
        >
          {title}
        </span>

        {actions}
      </div>

      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 27,
          fontWeight: 600,
          letterSpacing: "-0.03em",
          lineHeight: "32px",
          margin: "2px 0 0",
          textWrap: "balance",
        }}
      >
        {title}
      </h1>

      {subtitle && (
        <div style={{ fontSize: 12.5, color: "var(--text-secondary)", marginTop: 3 }}>
          {subtitle}
        </div>
      )}

      {/* Dove finisce il titolo grande: è questo che l'osservatore guarda. */}
      <div ref={sentinel} aria-hidden="true" style={{ height: 1 }} />

      <div style={{ height: "var(--space-4)" }} />
    </>
  );
}
